import { DivIcon } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useEffect, useRef, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer, Tooltip } from 'react-leaflet';
import { DropZone } from '../models/dropzone.model';
import { DropZoneLanding } from '../models/dropzonelanding.model';
import { LandingSymbol } from '../models/landingsymbol.model';
import dropzoneService from '../services/dropzone.service';
import './dropzone-view.component.css';
import WindArrow from './wind-arrow.component';
import EditableDecimalInput from '../shared/editable-decimal-input.component';
import MarkerAutoPopup from '../shared/marker-auto-popup.component';
import moment from 'moment';
import { DropZoneNamedPosition } from '../models/dropzonenamedposition.model';
import { popupConfirm } from '../shared/confirm';
import mainArrowImage from '../assets/main-arrow.png';
import symbol_T from '../assets/T.png';
import symbol_U_LEFT from '../assets/U_LEFT.png';
import symbol_U_RIGHT from '../assets/U_RIGHT.png';
import symbol_DISABLED from '../assets/DISABLED.png';
import symbol_ROSE from '../assets/rose.png';
import EditableNumberInput from '../shared/editable-number-input.component';
import EditableStringInput from '../shared/editable-string-input.component';

interface DropZoneViewProps {
  editMode: boolean;
}

function DropZoneView(props: DropZoneViewProps) {
  const mapRef = useRef(null);
  const firstUpdate = useRef(true);
  const [dropZone, setDropZone] = useState<DropZone | null>(null);
  const [dropZoneNamedPositions, setDropZoneNamedPositions] = useState<DropZoneNamedPosition[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [warnLastSave, setWarnLastSave] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [error, setError] = useState(false);
  const [editingField, setEditingField] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(15);
  const refCurrentZoom = useRef(15);
  refCurrentZoom.current = zoomLevel;

  useEffect(() => {
    if (firstUpdate.current) {
      loadNamedPositions();
      loadData();


      firstUpdate.current = false;
      // Set up an interval to call loadData() every 10 seconds
      setInterval(loadData, 10000);
    }
  }, []);

  function setMap() {
    if (!mapRef.current) {
      setTimeout(function () {
        setMap();
      }, 250);
    } else {
      (mapRef.current as any).on('zoomend', function (e: any) {
        try {
          const zoomLevelVal = (mapRef.current as any).getZoom();
          setZoomLevel(zoomLevelVal);
        } catch (err) {
        }
      });
    }
  }

  function isEditingData() {
    return editingField === true;
  }

  async function loadNamedPositions() {
    try {
      const data = await dropzoneService.getDropZoneNamedPositions();
      setDropZoneNamedPositions(data);
    } catch (err) {
      setErrorMsg('' + err);
      setError(true);
    }
  }

  async function loadData() {
    if (isEditingData()) {
      return;
    }
    if (error) {
      return;
    }
    try {
      const data = await dropzoneService.getDropZoneData();
      if (isEditingData()) {
        return;
      }
      if (data.lastUpdate != null && data.centerOpened === true) {
        const MAX_HOURS = 12;
        const lastUpdateDate = (new Date(data.lastUpdate as any)).getTime();
        const nowDate = new Date().getTime();
        const hours = Math.abs(nowDate - lastUpdateDate) / (60 * 60 * 1000);
        if (hours > MAX_HOURS) {
          if (props.editMode === false) {
            setErrorMsg(`Expired (last update : ${moment(data.lastUpdate).format('DD/MM/yyyy HH:mm:ss')})`);
            setError(true);
            return;
          } else {
            setWarnLastSave(true);
          }
        } else {
          setWarnLastSave(false);
        }
      } else {
        setWarnLastSave(false);
      }
      
      if (!data.visibleAircrafts || data.visibleAircrafts < 1 || data.visibleAircrafts > 5) {
        data.visibleAircrafts = 1;
      }

      if(!data.aircrafts) {
        data.aircrafts = [];
      }

      while(data.aircrafts.length < data.visibleAircrafts) {
        data.aircrafts.push({
          name: `Aircraft ${data.aircrafts.length + 1}`, 
          order: data.aircrafts.length + 1,
          groundSpeed: 0
        });
      }

      console.log('check data ' + data.cloudBaseFeet + ' ' + isNaN(data.cloudBaseFeet != null ? data.cloudBaseFeet : NaN));
      setDropZone(data);
      setError(false);
    } catch (err) {
      setErrorMsg('' + err);
      setError(true);
    }
  }

  async function saveData(dzData: DropZone) {
    const currData = dropZone;
    try {
      setSaving(true);
      setDropZone(dzData);
      const data = await dropzoneService.saveData(dzData);
      setDropZone(data);
      setWarnLastSave(false);
      setError(false);
      setSaving(false);
    } catch (err) {
      setDropZone(currData);
      setErrorMsg('' + err);
      setError(true);
      setSaving(false);
    }
  }

  function openDropzone() {
    if (dropZone != null) {
      dropZone.centerOpened = true;
      saveData(dropZone);
    }
  }

  function refresh() {
    window.location.reload();
  }

  function closeDropzone() {
    if (dropZone != null) {
      dropZone.centerOpened = false;
      saveData(dropZone);
    }
  }

  function addAirCraft() {
    if (dropZone != null) {
      if (!dropZone.aircrafts) {
        dropZone.aircrafts = [];
      }
      if (!dropZone.visibleAircrafts) {
        dropZone.visibleAircrafts = 0;
      }

      dropZone.visibleAircrafts++;

      if (dropZone.aircrafts.length < dropZone.visibleAircrafts) {
        dropZone.aircrafts.push({
          name: `Aircraft ${dropZone.aircrafts.length + 1}`,
          order: dropZone.aircrafts.length + 1,
          groundSpeed: 0
        });
      }
      saveData(dropZone);
    }
  }

  function removeAirCraft() {
    if (dropZone != null) {
      if (!dropZone.aircrafts) {
        dropZone.aircrafts = [];
      }
      if (!dropZone.visibleAircrafts || dropZone.visibleAircrafts <= 2) {
        dropZone.visibleAircrafts = 1;
      } else {
        dropZone.visibleAircrafts--;
      }

      saveData(dropZone);
    }
  }

  function getMainArrayIcon(rotation: number) {
    let w = 36;
    let h = 186;
    /*
    let ratio = refCurrentZoom.current/15;
    if(ratio>1) {
      ratio += 0.4 ;
    } else if(ratio <1) {
      ratio -= 0.4;
    }
    w *= ratio;
    h *= ratio;
    */
    // POSITION CHANGES WHEN ZOOM CHANGES !! :-(
    //console.log(w + ' ' + h + ' ' + ratio);

    return new DivIcon({
      iconAnchor: [w, h],
      popupAnchor: [-20, -h],
      className: 'rotated-marker',
      html: `<img src="${mainArrowImage}" style="width: ${w}px; height: ${h}px; transform: rotate(${rotation}deg);">`,
    });
  }

  function getLandingArrayIcon(symbol: LandingSymbol, rotation: number) {
    let html = '';
    if (LandingSymbol.T === symbol) {
      html = `<img src="${symbol_T}" style="width: 36px; height: 36px; transform: rotate(${rotation}deg);">`;
    } else if (LandingSymbol.U_LEFT === symbol) {
      html = `<img src="${symbol_U_LEFT}" style="width: 36px; height: 36px; transform: rotate(${rotation}deg);">`;
    } else if (LandingSymbol.DISABLED === symbol) {
      html = `<img src="${symbol_DISABLED}" style="width: 36px; height: 36px;">`;
    } else {
      html = `<img src="${symbol_U_RIGHT}" style="width: 36px; height: 36px; transform: rotate(${rotation}deg);">`;
    }

    let w = 36;
    let h = 36;
    /*
    let ratio = refCurrentZoom.current/15;
    if(ratio>1) {
      ratio += 0.4 ;
    } else if(ratio <1) {
      ratio -= 0.4;
    }
    w *= ratio;
    h *= ratio; 
    */
    return new DivIcon({
      iconAnchor: [w, h],
      popupAnchor: [-20, -h],
      className: 'rotated-marker',
      html: html
    });
  }

  function handleUpdateDropZoneMainArrowPos(newId: any) {
    const targetId = parseInt(newId);
    if (dropZone != null && dropZoneNamedPositions != null) {
      dropZoneNamedPositions.forEach(dzPos => {
        if (dzPos.id === targetId) {
          dropZone.mainArrowPosition = dzPos;
          saveData(dropZone).then(dzSaved => {
            if (dzSaved != null) {
              setDropZone(dzSaved);
            }
          });
        }
      });
    }
  }

  async function handleValueChange(fieldName: string, newVal: any, targetObject: any) {
    let dz = dropZone as any;
    if (dz != null && newVal != dz[fieldName]) {

      // Force 0 for all fields if NaN, except...
      if (isNaN(newVal)) {
        if (fieldName !== 'cloudBaseFeet' && fieldName !== 'name') {
          newVal = 0;
        }
      }

      if (fieldName === 'minJumps' && newVal > dz.minJumps) {
        const confirmation = await popupConfirm("Set Min Jumps to " + newVal + " ? That will remove jumpers that does not have enough jumps.", {
          okLabel: 'Yes',
          cancelLabel: 'No',
          title: 'New Minimum Jumps Limit'
        });
      
        if (confirmation.ok) {
          dz[fieldName] = newVal;
        } else {
          return;
        }
      } else {
        if(!targetObject) {
          dz[fieldName] = newVal;
        } else {
          targetObject[fieldName] = newVal;
        }
      }

      saveData(dz).then(dzSaved => {
        if (dzSaved != null) {
          setDropZone(dzSaved);
        }
      });
    }
  };

  function handleLandingZoneValueChange(zone: DropZoneLanding, fieldName: string, newVal: any) {
    if (dropZone != null && zone != null) {
      if (!dropZone.landingZones) {
        throw new Error('dropZone.landingZones is null !');
      }
      if (dropZone.landingZones.indexOf(zone) === -1) {
        throw new Error('zone not in dropZone!');
      }

      (zone as any)[fieldName] = newVal;

      saveData(dropZone).then(dzSaved => {
        if (dzSaved != null) {
          setDropZone(dzSaved);
        }
      });
    }
  };

  /*
   Waiting time seconds, for 300m of separation between groups
  */
  function getWaitingTimeSec(aircraftGroundSpeedKnots: number) {
    if (aircraftGroundSpeedKnots < 5) {
      return '--';
    }
    if (aircraftGroundSpeedKnots < 10) {
      return '119';
    }
    if (aircraftGroundSpeedKnots < 15) {
      return '60';
    }
    if (aircraftGroundSpeedKnots < 20) {
      return '40';
    }
    if (aircraftGroundSpeedKnots < 25) {
      return '30';
    }
    if (aircraftGroundSpeedKnots < 30) {
      return '24';
    }
    if (aircraftGroundSpeedKnots < 35) {
      return '20';
    }
    if (aircraftGroundSpeedKnots < 40) {
      return '17';
    }
    if (aircraftGroundSpeedKnots < 45) {
      return '15';
    }
    if (aircraftGroundSpeedKnots < 50) {
      return '14';
    }
    if (aircraftGroundSpeedKnots < 55) {
      return '12';
    }
    if (aircraftGroundSpeedKnots < 60) {
      return '11';
    }
    if (aircraftGroundSpeedKnots < 65) {
      return '10';
    }
    if (aircraftGroundSpeedKnots < 70) {
      return '10';
    }
    if (aircraftGroundSpeedKnots < 75) {
      return '9';
    }
    if (aircraftGroundSpeedKnots < 80) {
      return '8';
    }
    if (aircraftGroundSpeedKnots < 85) {
      return '8';
    }
    if (aircraftGroundSpeedKnots < 90) {
      return '7';
    }
    if (aircraftGroundSpeedKnots < 95) {
      return '7';
    }
    if (aircraftGroundSpeedKnots < 100) {
      return '7';
    }
    return '6';
  }

  return (
    error ? <div className="message-container">
      <div style={{ color: "red", fontSize: "20px", textAlign: "center" }}>
        <div className="red-message-top">{errorMsg}</div>
        <input type="button"
          className="open-close-dropzone-button"
          onClick={refresh}
          value={"Reload page"} />
      </div>
    </div>
      :
      dropZone != null && dropZone.centerOpened != true ?
        <div className="message-container">
          <div className="red-message-top">The dropzone is currently closed</div>
          {props.editMode ? <div style={{ textAlign: 'center' }}>
            <input type="button"
              className="open-close-dropzone-button"
              onClick={openDropzone}
              value={"Open DZ"} />
          </div>
            : null}
        </div>
        :
        <>
          {saving ?
            <div className="saving-div">
              <div className="saving-text">Saving</div>
              <div className="saving-loader">
                <span className="loader__element"></span>
                <span className="loader__element"></span>
                <span className="loader__element"></span>
              </div>
            </div> : null}

          <div className={'dropzone-header aircrafts-' + (dropZone?.visibleAircrafts ?? 1)}>
            <div className="dropzone-headerBox">
              <div className="dropzone-headerBoxIcon">
                <img src="/assets/cloud-base-altitude.svg"></img>
              </div>
              <div className="dropzone-headerBoxContent">
                <EditableNumberInput editable={props.editMode}
                  inputDivClassName='input-cloud-base-feet-div'
                  inputClassName='input-cloud-base-feet'
                  spanDivClassName='span-cloud-base-feet-div'
                  spanClassName='span-cloud-base-feet'
                  onEditStart={() => setEditingField(true)}
                  onEditEnd={() => setEditingField(false)}
                  onValueChanged={(val: number) => handleValueChange('cloudBaseFeet', val, null)}
                  value={dropZone != null && dropZone.cloudBaseFeet != null ? dropZone.cloudBaseFeet : NaN}
                  inputTextAfter='ft'
                  spanTextAfter='ft'
                >
                </EditableNumberInput>
              </div>
            </div>

            <div className="dropzone-headerBox">
              <div className="dropzone-headerBoxIcon">
                <img src="/assets/wind.svg"></img>
              </div>
              <div className="dropzone-headerBoxContent">
                <table className="wind-table">
                  <tbody>
                    <tr>
                      <td><span>5 000 ft</span></td>
                      <td>
                        <div className="wind-table-force-direction">
                          <EditableNumberInput editable={props.editMode}
                            onEditStart={() => setEditingField(true)}
                            onEditEnd={() => setEditingField(false)}
                            onValueChanged={(val: number) => handleValueChange('wind5000Knots', val, null)}
                            value={dropZone != null && dropZone.wind5000Knots != null ? dropZone.wind5000Knots : 0}
                            inputTextAfter='kn'
                            spanTextAfter='kn'
                            inputClassName='input-wind'
                          />
                          <WindArrow rotation={dropZone?.wind5000Degrees || 0}
                            editable={props.editMode}
                            inputClassName='input-degrees'
                            onRotationChanged={(val: number) => handleValueChange('wind5000Degrees', val, null)}
                          />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td><span>3 000 ft</span></td>
                      <td>
                        <div className="wind-table-force-direction">
                          <EditableNumberInput editable={props.editMode}
                            onEditStart={() => setEditingField(true)}
                            onEditEnd={() => setEditingField(false)}
                            onValueChanged={(val: number) => handleValueChange('wind3000Knots', val, null)}
                            value={dropZone != null && dropZone.wind3000Knots != null ? dropZone.wind3000Knots : 0}
                            inputTextAfter='kn'
                            spanTextAfter='kn'
                            inputClassName='input-wind'
                          />
                          <WindArrow rotation={dropZone?.wind3000Degrees || 0}
                            editable={props.editMode}
                            inputClassName='input-degrees'
                            onRotationChanged={(val: number) => handleValueChange('wind3000Degrees', val, null)} />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td><span>1 500 ft</span></td>
                      <td>
                        <div className="wind-table-force-direction">
                          <EditableNumberInput editable={props.editMode}
                            onEditStart={() => setEditingField(true)}
                            onEditEnd={() => setEditingField(false)}
                            onValueChanged={(val: number) => handleValueChange('wind1500Knots', val, null)}
                            value={dropZone != null && dropZone.wind1500Knots != null ? dropZone.wind1500Knots : 0}
                            inputTextAfter='kn'
                            spanTextAfter='kn'
                            inputClassName='input-wind'
                          />
                          <WindArrow rotation={dropZone?.wind1500Degrees || 0}
                            editable={props.editMode}
                            inputClassName='input-degrees'
                            onRotationChanged={(val: number) => handleValueChange('wind1500Degrees', val, null)} />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="dropzone-headerBox">
              <div className="dropzone-headerBoxIcon">
                <img src="/assets/min-jumps.svg"></img>
              </div>
              <div className="dropzone-headerBoxContent">
                <EditableNumberInput editable={props.editMode}
                  inputDivClassName='input-min-jumps-div'
                  inputClassName='input-min-jumps'
                  spanDivClassName='span-min-jumps-div'
                  spanClassName={'span-min-jumps'}
                  onEditStart={() => setEditingField(true)}
                  onEditEnd={() => setEditingField(false)}
                  onValueChanged={(val: number) => handleValueChange('minJumps', val, null)}
                  value={dropZone != null && dropZone.minJumps != null ? dropZone.minJumps : 0}
                  maxLength={4}
                  inputTextAfter='jumps'
                  spanTextAfter='jumps'
                />
              </div>
            </div>

            {Array.from({ length: dropZone?.visibleAircrafts || 1 }, (_, aircraftIndex) => {
              const aircraft = dropZone?.aircrafts?.[aircraftIndex];

              return (
                <div className="dropzone-headerBox">
                  <div className="dropzone-headerBoxIcon">
                    <img src="/assets/skydiving.png"></img>
                  </div>
                  <div className="dropzone-headerBoxContent">
                      <EditableStringInput editable={props.editMode}
                        inputDivClassName='input-aircraft-name-div'
                        inputClassName='input-aircraft-name'
                        spanDivClassName='aircraft-name-span-div'
                        spanClassName='span-aircraft-name'
                        onEditStart={() => setEditingField(true)}
                        onEditEnd={() => setEditingField(false)}
                        onValueChanged={(val: string) => handleValueChange('name', val, aircraft)}
                        value={aircraft?.name ?? ''}
                      >
                      </EditableStringInput>
                    <span className="span-sec">{getWaitingTimeSec(aircraft?.groundSpeed ?? 0)} sec</span>
                    <div>
                      <EditableNumberInput editable={props.editMode}
                        inputDivClassName='input-ground-speed-div'
                        inputClassName='input-ground-speed'
                        spanDivClassName='ground-speed-span-div'
                        spanClassName='span-ground-speed'
                        onEditStart={() => setEditingField(true)}
                        onEditEnd={() => setEditingField(false)}
                        onValueChanged={(val: number) => handleValueChange('groundSpeed', val, aircraft)}
                        value={aircraft?.groundSpeed ?? 0}
                        inputTextAfter='kn'
                        spanTextAfter='kn'
                      >
                      </EditableNumberInput>
                    </div>

                    {aircraftIndex === (dropZone?.visibleAircrafts ?? 1) - 1 ?
                          <div className="last-update-actions-div">
                            <div className="last-update-div">
                              <span style={{
                                color: warnLastSave ? 'red' : '',
                                fontWeight: warnLastSave ? 'bold' : ''
                              }}>Last update : {moment(dropZone?.lastUpdate).format('DD/MM/yyyy HH:mm:ss')}</span>
                            </div>

                            {props.editMode ?
                                <div className="action-buttons-div">
                                  {(dropZone?.visibleAircrafts ?? 0) > 1 ? 
                                    <input type="button"
                                    className="add-aircraft-button"
                                    onClick={removeAirCraft}
                                    value={"x"} />: null}

                                  {(dropZone?.visibleAircrafts ?? 0) < 4 ? 
                                  <input type="button"
                                    className="add-aircraft-button"
                                    onClick={addAirCraft}
                                    value={"+"} />:null}

                                  <input type="button"
                                    className="open-close-dropzone-button"
                                    onClick={closeDropzone}
                                    value={"Close DZ"} />
                                </div>
                              : null}
                            </div>
                        : null}
                  </div>
                </div>
              );
            })}
          </div>

          <div className={'dropzone-map'}>
            {dropZone && (
              <MapContainer
                ref={mapRef}
                whenReady={setMap}
                center={[dropZone?.mapLatitude || 0, dropZone?.mapLongitude || 0]}
                zoom={zoomLevel}
                className={'dropzone-map-container aircrafts-' + (dropZone?.visibleAircrafts ?? 1)}
                attributionControl={false}
                maxZoom={15}
                minZoom={15}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" /* "https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png */
                />

                {/* Main Arrow */}
                <MarkerAutoPopup position={[dropZone?.mainArrowPosition?.latitude || 0, dropZone?.mainArrowPosition?.longitude || 0]}
                  icon={getMainArrayIcon(dropZone?.mainArrowDegrees || 0)}
                >
                  <Popup autoClose={false} closeOnClick={false} closeButton={true} >
                    <div>
                      {props.editMode === false ?
                        <div>
                          <span className='landing-zone-title'>{dropZone?.mainArrowPosition?.name}</span>

                          {dropZone?.mainArrowPosition?.alertMessage ?
                            <div className='alert-message'>{dropZone?.mainArrowPosition?.alertMessage}</div>
                            : null}
                        </div>
                        :
                        <div>
                          <select className='landing-zone-title'
                            defaultValue={dropZone?.mainArrowPosition?.id}
                            onChange={e => {
                              handleUpdateDropZoneMainArrowPos(e.target.value)
                            }}
                          >
                            {dropZoneNamedPositions?.map((namedPos: DropZoneNamedPosition) => (
                              <option key={namedPos.id} value={namedPos.id}>{namedPos.name}</option>
                            ))}
                          </select>
                          {dropZone?.mainArrowPosition?.alertMessage ?
                            <div className='alert-message'>{dropZone?.mainArrowPosition?.alertMessage}</div>
                            : null}
                        </div>
                      }

                      <div>
                        <EditableDecimalInput editable={props.editMode}
                          onEditStart={() => setEditingField(true)}
                          onEditEnd={() => setEditingField(false)}
                          onValueChanged={(val: number) => handleValueChange('dropStartDistance', val, null)}
                          value={dropZone?.dropStartDistance || 0}
                          inputTextBefore='Drop start distance'
                          inputTextAfter='nm'
                          spanTextBefore='Drop start distance :'
                          spanTextAfter='nm'
                          inputClassName='input-landing'
                        >
                        </EditableDecimalInput>

                        <EditableDecimalInput editable={props.editMode}
                          onEditStart={() => setEditingField(true)}
                          onEditEnd={() => setEditingField(false)}
                          onValueChanged={(val: number) => handleValueChange('mainArrowDegrees', val, null)}
                          value={dropZone?.mainArrowDegrees || 0}
                          inputTextBefore='Degrees'
                          inputTextAfter='°'
                          spanTextBefore='Degrees :'
                          spanTextAfter='°'
                          inputClassName='input-landing'
                        >
                        </EditableDecimalInput>
                      </div>
                    </div>

                  </Popup>

                </MarkerAutoPopup>

                {/* Landing Zones */}
                {dropZone?.landingZones?.map((landingZone: DropZoneLanding) => (
                  <Marker
                    key={landingZone.id}
                    position={[landingZone?.latitude || 0, landingZone?.longitude || 0]}
                    icon={getLandingArrayIcon(landingZone?.landingSymbol || LandingSymbol.U_LEFT, landingZone?.degrees || 0)}
                  >
                    <Popup closeOnClick={false} closeButton={true}>
                      <div>
                        <div>
                          <span className='landing-zone-title'>{landingZone.name}</span>
                        </div>
                        {props.editMode ?
                          <div>
                            {/* Symbol */}
                            <select defaultValue={landingZone.landingSymbol}
                              onChange={e => {
                                handleLandingZoneValueChange(landingZone, 'landingSymbol', e.target.value)
                              }}
                            >
                              <option value={LandingSymbol.T}>{'' + LandingSymbol.T}</option>
                              <option value={LandingSymbol.U_LEFT}>{'' + LandingSymbol.U_LEFT}</option>
                              <option value={LandingSymbol.U_RIGHT}>{'' + LandingSymbol.U_RIGHT}</option>
                              <option value={LandingSymbol.DISABLED}>{'' + LandingSymbol.DISABLED}</option>
                            </select>

                            <EditableDecimalInput editable={props.editMode}
                              onEditStart={() => setEditingField(true)}
                              onEditEnd={() => setEditingField(false)}
                              onValueChanged={(val: number) => handleLandingZoneValueChange(landingZone, 'longitude', val)}
                              value={landingZone.longitude || 0}
                              inputTextBefore='Longitude'
                              inputTextAfter='°'
                              spanTextBefore='Longitude :'
                              spanTextAfter='°'
                              inputClassName='input-landing'
                            >
                            </EditableDecimalInput>

                            <EditableDecimalInput editable={props.editMode}
                              onEditStart={() => setEditingField(true)}
                              onEditEnd={() => setEditingField(false)}
                              onValueChanged={(val: number) => handleLandingZoneValueChange(landingZone, 'latitude', val)}
                              value={landingZone.latitude || 0}
                              inputTextBefore='Latitude'
                              inputTextAfter='°'
                              spanTextBefore='Latitude :'
                              spanTextAfter='°'
                              inputClassName='input-landing'
                            >
                            </EditableDecimalInput>

                            <EditableDecimalInput editable={props.editMode}
                              onEditStart={() => setEditingField(true)}
                              onEditEnd={() => setEditingField(false)}
                              onValueChanged={(val: number) => handleLandingZoneValueChange(landingZone, 'degrees', val)}
                              value={landingZone.degrees || 0}
                              inputTextBefore='Degrees'
                              inputTextAfter='°'
                              spanTextBefore='Degrees :'
                              spanTextAfter='°'
                              inputClassName='input-landing'
                            >
                            </EditableDecimalInput>
                          </div>
                          : null}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            )}
            <div className='contributors-div' >
              <a href="https://www.openstreetmap.org/copyright" target="_blank">&copy; OpenStreetMap</a>
            </div>
            <div className='dropzone-footer'>
              <span style={{ fontWeight: 'bold' }}>Ground Operator{dropZone != null && dropZone.groundOperatorsNames != null && dropZone.groundOperatorsNames.length > 1 ? 's : ' : ' : '}</span><span>{dropZone?.groundOperatorsNames?.join(', ')}</span> &nbsp;
              <span style={{ fontWeight: 'bold' }}>Pilot{dropZone != null && dropZone.pilotsNames != null && dropZone.pilotsNames.length > 1 ? 's : ' : ' : '}</span><span>{dropZone?.pilotsNames?.join(', ')}</span>
            </div>

            <div
              style={{
                position: 'absolute',
                top: '5px',
                right: '5px',
                zIndex: 1000, // Ensure it's above the map
              }}
            >
              <img src={symbol_ROSE} style={{ height: 'calc(100Vh / 7)' }} />
            </div>
          </div>
        </>
  )
}

export default DropZoneView;